import React, { useCallback } from 'react';
import { styled } from '@compiled/react';
import { Bleed, Box, Inline } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import AddIcon from '@atlaskit/icon/core/add';
import { cssMap } from '@atlaskit/css';
import { useIntl } from '@atlassian/jira-intl';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import messages from '../../../messages.tsx';
import { useTeamPermissions } from '../../../services/use-team-permissions/index.tsx';

const styles = cssMap({
	iconWrapper: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		paddingTop: token('space.075'),
		paddingRight: token('space.075'),
		paddingBottom: token('space.075'),
		paddingLeft: token('space.075'),
	},
	optionWrapper: {
		cursor: 'pointer',
	},
});

type CreateTeamOptionProps = {
	onClick: () => void;
	isFocused: boolean;
};

export function CreateTeamOption({ onClick, isFocused }: CreateTeamOptionProps) {
	const { formatMessage } = useIntl();
	const formattedCreateTeamText = formatMessage(messages.createTeamButtonText);
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const handleCreateTeamOptionClick = useCallback(() => {
		fireUIAnalytics(createAnalyticsEvent({}), 'button clicked', 'createTeamInline', {
			teamsFieldEmptyState: false,
		});
		onClick();
	}, [onClick, createAnalyticsEvent]);

	const { canCreateTeams } = useTeamPermissions();

	return canCreateTeams ? (
		// eslint-disable-next-line jira/restricted/react-suspense
		<React.Suspense fallback={null}>
			<Bleed inline="space.200" block="space.100">
				<CreateButtonBorder />
				<Container
					onClick={handleCreateTeamOptionClick}
					isFocused={isFocused}
					data-testid="issue-field-team.ui.edit-mode.create-team-option.create-team-inline-option"
				>
					<OptionDataWrapper>
						<Inline xcss={styles.optionWrapper} space="space.100" alignBlock="center">
							<Box xcss={styles.iconWrapper}>
								<AddIcon label="" spacing="spacious" />
							</Box>
							{formattedCreateTeamText}
						</Inline>
					</OptionDataWrapper>
				</Container>
			</Bleed>
		</React.Suspense>
	) : null;
}

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const CreateButtonBorder = styled.div({
	borderTop: `1px solid ${token('color.border')}`,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Container = styled.div<{ isFocused: boolean }>({
	marginTop: token('space.025'),
	width: '100%',
	height: '100%',
	'&:hover': {
		backgroundColor: token('color.background.neutral.subtle.hovered'),
		boxShadow: `inset 2px 0px 0px ${token('color.border.selected')}`,
	},
	// isFocused
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	backgroundColor: ({ isFocused }) =>
		isFocused ? token('color.background.neutral.subtle.hovered') : 'transparent',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	boxShadow: ({ isFocused }) =>
		isFocused ? `inset 2px 0px 0px ${token('color.border.selected')}` : 'none',
});

// Matching spacing of other options
// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const OptionDataWrapper = styled.div({
	paddingTop: token('space.075'),
	paddingRight: token('space.150'),
	paddingBottom: token('space.075'),
	paddingLeft: token('space.150'),
});
